import { useCallback, useEffect, useState } from 'react';
import { Card } from './Card';
import {
  getPushState,
  pushDiagnostics,
  refreshSubscription,
  sendTestPush,
  subscribePush,
  unsubscribePush,
  type PushState,
} from '../lib/push';

export function PushCard() {
  const [state, setState] = useState<PushState>({ status: 'unsupported' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setState(await getPushState());
  }, []);

  useEffect(() => {
    void refresh();
    void refreshSubscription();
  }, [refresh]);

  const enable = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const next = await subscribePush();
      setState(next);
      if (next.status === 'subscribed') setMsg('Subscribed.');
      if (next.status === 'denied') setMsg('Permission denied. Enable in iOS settings → Notifications.');
    } catch (e) {
      setMsg(String(e));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const next = await unsubscribePush();
      setState(next);
      setMsg('Unsubscribed.');
    } catch (e) {
      setMsg(String(e));
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await sendTestPush();
      setMsg(`Sent to ${r.sent} subscription${r.sent === 1 ? '' : 's'}. Check the lock screen.`);
    } catch (e) {
      setMsg(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Notifications" hint="The app's reminders only fire when push is on.">
      <Status state={state} />
      <div className="flex flex-wrap gap-2 mt-2">
        {state.status === 'idle' && (
          <button onClick={enable} disabled={busy} className="btn-primary px-5 py-2.5 text-sm">
            Enable push
          </button>
        )}
        {state.status === 'subscribed' && (
          <>
            <button onClick={test} disabled={busy} className="btn-primary px-5 py-2.5 text-sm">
              Send test push
            </button>
            <button onClick={disable} disabled={busy} className="btn-secondary px-5 py-2.5 text-sm">
              Disable
            </button>
          </>
        )}
        {state.status === 'denied' && (
          <p className="text-sm text-warn">Notifications blocked. Enable in OS settings.</p>
        )}
      </div>
      {msg && <p className="text-xs text-fg-muted mt-1">{msg}</p>}
    </Card>
  );
}

function Status({ state }: { state: PushState }) {
  switch (state.status) {
    case 'unsupported': {
      const d = pushDiagnostics();
      return (
        <>
          <p className="text-sm text-fg-muted">This browser does not support web push.</p>
          <details className="mt-1 text-[11px] text-fg-faint">
            <summary className="cursor-pointer">Why?</summary>
            <ul className="mt-1 font-mono leading-snug">
              <li>iOS: {String(d.isIOS)}</li>
              <li>standalone: {String(d.isStandalone)}</li>
              <li>serviceWorker: {String(d.hasServiceWorker)}</li>
              <li>PushManager: {String(d.hasPushManager)}</li>
              <li>Notification: {String(d.hasNotification)}</li>
              <li>permission: {d.notificationPermission}</li>
              <li className="break-all">UA: {d.userAgent}</li>
            </ul>
          </details>
        </>
      );
    }
    case 'needs_install':
      return (
        <p className="text-sm text-fg-muted">
          Add this app to your home screen first. On iOS: Share → Add to Home Screen, then open the
          installed app.
        </p>
      );
    case 'denied':
      return <p className="text-sm text-warn">Permission denied — push won't fire until you re-enable.</p>;
    case 'idle':
      return <p className="text-sm text-fg-muted">Push not yet enabled on this device.</p>;
    case 'subscribed':
      return <p className="text-sm text-good">Push enabled · daily reminders will fire.</p>;
  }
}
